import { cn } from 'helpers/classnames';
import ReturnLegalVerbiage from 'components/account/Returns/ReturnLegalVerbiage';
import ZawRefundSummaryMessage from 'components/account/Returns/ZawRefundSummaryMessage';
import Tooltip from 'components/common/Tooltip';
import { SmallLoader } from 'components/Loader';
import { toUSD } from 'helpers/NumberFormats';
import { pluralize } from 'helpers';
import useMartyContext from 'hooks/useMartyContext';
import refundSummaryUtils from 'utils/refundSummary';
import InfoOutlineSmallIcon from 'components/common/IconsV2/InfoOutlineSmallIcon';

import css from 'styles/components/account/returns/refundSummary.scss';

const CO_FEE_TOOLTIP_COPY =
  'Colorado imposes a retail delivery fee on all taxable orders of tangible personal property delivered by motor vehicle to the state. The fee is non-refundable.';

const RefundSummary = ({
  items,
  boxNum,
  singleBox,
  isPrivateLabel = false,
  isZAWReturnsEnabled,
  zawPromotions,
  ordersMap,
  isLoadingZawPromotions,
  phoneNumber,
  zawSubsidyReissueEligibility,
  returnStep
}) => {
  const {
    marketplace: {
      dataMaskPiiElements,
      returns: { returnPolicyUrl, handlesReturnShipping }
    }
  } = useMartyContext();

  if (!items) {
    return '';
  }

  if (isZAWReturnsEnabled && isLoadingZawPromotions) {
    return <SmallLoader />;
  }

  const heading = refundSummaryUtils.makeHeading(singleBox, boxNum);
  const itemsTitle = refundSummaryUtils.makeTitle(items);
  const totals = refundSummaryUtils.makeTotals(items, isZAWReturnsEnabled, zawPromotions, ordersMap);
  const isVisible = refundSummaryUtils.createSummaryVisibility(isZAWReturnsEnabled, ordersMap, items, totals);

  const coFeeTooltip = isVisible.coloradoFee ? <ColoradoFeeTooltip /> : null;

  return (
    <section className={css.container}>
      {!isPrivateLabel && <h2 className="zappos:heading-s">{heading}</h2>}

      <div className={cn(css.details, 'zappos:body-s')} role="table" aria-label={heading} data-cs-mask={dataMaskPiiElements}>
        <p className="zappos:body-s-bold">{pluralize('Item', items.length)} you are returning</p>

        <DetailEntry field={itemsTitle} value={totals.subtotal} />

        <DetailEntry isPromo field="Zappos At Work Subsidy†:" value={totals.zawSubsidies} visible={isVisible.zawSubsidy} />

        <DetailEntry isPromo field="Zappos At Work Discount:" value={totals.zawDiscounts} visible={isVisible.zawDiscount} />

        <DetailEntry isPromo field="Other Promotions Applied:" value={totals.otherPromos} visible={isVisible.otherPromos} />

        <DetailEntry isPromo field="Promotions Applied:" value={totals.promos} visible={isVisible.promos} />

        <DetailEntry field="Collected Tax:" value={totals.tax} />

        <DetailEntry field="Retail Delivery Fees:" value={totals.coloradoFee} visible={isVisible.coloradoFee} tooltip={coFeeTooltip} />

        <DetailEntry field="Return Shipping:" value="FREE" visible={!!handlesReturnShipping} />

        <DetailEntry field="You Paid:" value={totals.total} className={cn(css.paidAmount, 'zappos:body-s-bold')} />
      </div>

      <div className={css.refundContainer}>
        <DetailEntry
          field="Refund Total*"
          value={totals.refundTotal}
          className={cn(css.refund, 'zappos:body-l-bold')}
          dataMaskPiiElements={dataMaskPiiElements}
        />
        <ZawRefundSummaryMessage
          visible={isVisible.zawSubsidyMessage}
          zawSubsidyReissueEligibility={zawSubsidyReissueEligibility}
          phoneNumber={phoneNumber}
          subsidyAmount={Math.abs(totals.zawSubsidies)}
          promotions={zawPromotions}
          returnStep={returnStep}
        />
      </div>

      {isPrivateLabel && (
        <div className={css.privateLabelLegal}>
          <ReturnLegalVerbiage returnPolicyUrl={returnPolicyUrl} />
        </div>
      )}
    </section>
  );
};

const ColoradoFeeTooltip = () => (
  <Tooltip
    tooltipId="coloradoFeeTooltip"
    wrapperClassName={css.tooltipWrapper}
    tooltipClassName={css.tooltip}
    direction="left"
    content={CO_FEE_TOOLTIP_COPY}
  >
    <InfoOutlineSmallIcon size={16} />
  </Tooltip>
);

const DetailEntry = ({ field, value, className, visible = true, isPromo, tooltip, dataMaskPiiElements }) => {
  if (!visible) {
    return null;
  }

  if (typeof value === 'number') {
    value = isPromo ? `(${toUSD(Math.abs(value))})` : toUSD(value);
  }

  return (
    <div className={className} role="row" data-cs-mask={dataMaskPiiElements}>
      <span title={field} role="cell" className={cn({ [css.withTooltip]: Boolean(tooltip) })}>
        {field}
        {tooltip && <> {tooltip}</>}
      </span>
      <span role="cell">{value}</span>
    </div>
  );
};

RefundSummary.displayName = 'RefundSummary';

export default RefundSummary;
